import { useMemo, ReactNode } from 'react';

// material-ui
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { createTheme, ThemeOptions, ThemeProvider, Theme } from '@mui/material/styles';

// project import
import useConfig from 'hooks/useConfig';
import Palette from './palette';
import Typography from './typography';

import componentStyleOverrides from './compStyleOverride';
import customShadows from './shadows';

// types
import { CustomShadowProps } from 'types/default-theme';
import { TypographyOptions } from '@mui/material/styles/createTypography';

interface Props {
    children: ReactNode;
}

export default function ThemeCustomization({ children }: Props) {
    const { borderRadius, fontFamily, navType, outlinedFilled, presetColor, rtlLayout } = useConfig();

    const theme: Theme = useMemo<Theme>(() => Palette(navType, presetColor), [navType, presetColor]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const themeTypography: TypographyOptions = useMemo<TypographyOptions>(
        () => Typography(theme, borderRadius, fontFamily),
        [theme, borderRadius, fontFamily]
    );
    const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(() => customShadows(navType, theme), [navType, theme]);

    const themeOptions: ThemeOptions = useMemo(
        () => ({
            direction: rtlLayout ? 'rtl' : 'ltr',
            palette: theme.palette,
            mixins: {
                toolbar: {
                    minHeight: '38px',
                    padding: '5px 16px',
                    '@media (min-width: 600px)': {
                        minHeight: '48px'
                    }
                },
                button_bs:{
                    p:0,
                    height:34,
                    minWidth:34,
                    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : '#fff',
                    color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.main,
                    border: '1px solid',
                    borderColor: theme.palette.grey[200],
                    '&:hover': {
                        background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.grey[100],
                        color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.dark,
                        borderColor: theme.palette.grey[400],
                    }
                },
                scrollbar_bs:{
                    // '.bs-scroll::-webkit-scrollbar': { width: 10, height: 10 },        // specificity bump via &&
                    // '.bs-scroll::-webkit-scrollbar-track': { background: 'transparent' },
                    // '.bs-scroll::-webkit-scrollbar-thumb': {
                    // backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.35)',
                    // borderRadius: 8,
                    // border: '3px solid transparent',
                    // backgroundClip: 'content-box',
                    // },
                    // '.bs-scroll::-webkit-scrollbar-thumb:hover': { backgroundColor: 'green !important', },
                    // '.bs-scroll::-webkit-scrollbar-thumb:active': { backgroundColor: 'red !important', },
                    scrollbarGutter: 'stable both-edges',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,.55)' : 'rgba(0,0,0,.15)'} transparent`,
                    colorScheme: theme.palette.mode,
                   
                }
            },
            typography: themeTypography,
            customShadows: themeCustomShadows
        }),
        [rtlLayout, theme, themeCustomShadows, themeTypography]
    );

    const themes: Theme = createTheme(themeOptions);
    themes.components = useMemo(
        () => componentStyleOverrides(themes, borderRadius, outlinedFilled),
        [themes, borderRadius, outlinedFilled]
    );

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
